<template>
	<div
		class="player-wrapper js-player-wrapper"
		:class="{ mobile: isMobileWebApp, landscape: isMobileLandscape, portrait: isMobilePortrait }"
		data-testid="player-wrapper"
	>
		<div
			ref="player-wrapper-container"
			class="player-wrapper-container"
		>
			<Player
				v-if="streamURL && !isCastingOnChromecast"
				ref="player"
				:autoplay="autoplay"
				:forced-duration="forcedDuration"
				:stream-lead-time-seconds="streamLeadTimeSeconds"
				:url="streamURL"
				:vast-url="vastURL"
				:ads-disabled="adsDisabled"
				:primary-pre-roll-ad-url="$arenaConfig.primaryPreRollAdUrl"
				:poster="event.imageUrl"
				:initial-volume="volumeLevel"
				:was-muted-manually="wasMutedManually"
				:force-muted="forceMuted"
				:loop="loop"
				:video-object-fit="isLoopBackgroundMode ? 'cover' : 'contain'"
				:google-ima-is-active="googleImaIsActive"
				@ad-skipped="handleAdSkipped($event.watchedTillEnd)"
				@ad-play="handleAdPlay"
				@ad-time="handleAdTime"
				@duration="handleDurationUpdate"
				@media-duration="handleMediaDurationUpdate"
				@error="handleError"
				@error-clear="handleErrorClear"
				@first-frame-loaded="handleFirstFrameLoaded"
				@position="handlePositionUpdate"
				@quality-levels="handleQualityLevelsUpdate"
				@audio-tracks-update="handleAudioTracksUpdate"
				@audio-track-change="handleAudioTrackChange"
				@ready="handleReady"
				@speed="handleSpeedUpdate"
				@state="handleStateUpdate"
				@airplay-availability-change="handleAirplay"
				@picture-in-picture-availability-change="handlePictureInPictureAvailabilityChange"
				@autoplayed-muted="handleAutoplayedMuted"
				@mute="handleMuted"
				@unmute="handleUnmuted"
				@volume="handleSetVolume"
				@core-player-ready="handleCorePlayerReady"
				@video-end-position-reached="handleVideoEndPositionReached"
				@preroll-ad-ready="handlePrerollAdReady"
				@ad-link-change="handleAdLinkChange"
				@set-google-ima-active="handleSetGoogleImaActive"
				@takeover-stage-rect-change="handleTakeoverStageRectChange"
			>
				<template #over="{ player }">
					<MonetizationTakeoverRenderer
						v-if="hasTakeoverAd"
						:position="player.position"
						:player-is-ad-mode="playerIsAdMode"
						:event="event"
						:ads="ads"
						:is-couch="isCouch"
						@state-change="handleTakeoverAdStateChange(player, $event)"
					/>
					<PlayerOverlays
						v-if="playerUiConfig.overlays.enabled"
						ref="playerOverlays"
						:overlay-ui-config="playerUiConfig.overlays"
						:scrubber-dragging="scrubberDragging"
						:should-show-player-error-toast="shouldShowPlayerErrorToast"
						:should-show-stream-ends-early-snackbar="shouldShowStreamEndsEarlySnackbar"
						:is-touch-device="isTouchDevice"
						:is-mobile-web-app="isMobileWebApp"
						:is-cinema-mode="isCinemaMode"
						:is-fullscreen="isFullscreen"
						:is-embedded="isEmbedded"
						:force-show-interaction-overlay="forceOverlayVisible"
						:force-disable-interaction-overlay="isTaggingToolMode && !isTouchDevice"
						:custom-overlay-image="customOverlayImage"
						:click-interaction-enabled="clickInteractionEnabled"
						:game-information="{
							position: player.position,
							event: event,
							tags: tags,
							showScoreboardScore: playerUiConfig.overlays.gameInformation.scoreBoard.enabled,
							showScoreboardTime: playerUiConfig.overlays.gameInformation.gameClock.enabled,
							playerCustomText: customText,
							playerCustomTextEnabled: playerUiConfig.overlays.gameInformation.customText.enabled,
						}"
						:player-content-info="{
							event: event,
						}"
						:back-button="{
							backUrl: backUrl,
							isFullscreen: isFullscreen,
						}"
						:product-placement-info="{
							isPlaying: player.state === PLAYER_STATE.PLAYING,
							isLive: isLive,
							duration: player.duration,
							elapsed: player.position,
						}"
						:video-interaction-feedback-overlay="{
							forcePlayVisible: forcePlayVisible,
							touchMode: playerUiConfig.overlays.videoInteractionFeedback.touchMode,
							hidePlayPause: !playerUiConfig.overlays.videoInteractionFeedback.playPause.enabled,
							isPlaying: player.state === PLAYER_STATE.PLAYING,
						}"
						:play-toggle-overlay="{
							isPlaying: player.state === PLAYER_STATE.PLAYING,
						}"
						:monetization-image-renderer="{
							position: player.position,
							playerIsAdMode: playerIsAdMode,
							playerWidth: playerWidth,
							event: event,
							ads: ads,
							isCouch: isCouch,
						}"
						:takeover-ad="takeoverAd"
						:takeover-stage-rect="takeoverStageRect"
						:skip-ad="{
							adRemainingTime: player.adDuration - player.adPosition,
							adSkipAllowed: player.currentAdIsSkippable,
							canSkipAd: player.currentAdIsSkippableAtCurrentPosition,
							adSkipTime: player.currentAdSkipTime,
						}"
						:game-event-overlay="{
							playerWidth: playerWidth,
							...currentGraphicsConfiguration,
						}"
						:autoplay-muted-indicator="{
							disableDismiss: playerUiConfig.overlays.autoplayMutedIndicator.disableDismiss,
							localIsMobile: isMobileWebApp,
						}"
						:timeline-block="{
							duration: player.duration,
							elapsed: player.position,
							isLookingBack: isLookingBackInLiveEvent,
						}"
						:player-controls="playerControlsProps"
						:paywall-preview-overlay="{
							previewLimitReached: previewLimitReached,
							initialPreviewDuration: initialPreviewDuration,
							event: event,
							isEmbedded: isEmbedded,
						}"

						@close-couch-mode="closeCouchMode"
						@skip-ad="player.skipAd"
						@back-to-live="seekToLive"
						@autoplay-muted-indicator-click="handleAutoplayedMutedClick"
						@paywall-preview-limit-reached="handlePreviewLimitReached"
						@paywall-preview-started="handlePreviewStarted"
						@cancel-error-toast="cancelErrorToast"
						@single-tap="handleSingleTap"
						@double-tap="handleDoubleTap"
						@triple-tap="handleTripleTap"
						@interaction-overlay-visibility-change="handleInteractionOverlayVisibilityChange"
						@overlay-click="handleOverlayClick"
						@touch-toggle-play-pause="handleTouchTogglePlayPause"
						@close-fullscreen="toggleFullscreen"
						v-on="playerControlsEvents"
					/>
				</template>
				<template #after="{ player }">
					<MonetizationImageRenderer
						v-if="isMobileWebAppPortrait && !takeoverAd?.visible"
						:type="AdType.PLAYER_BANNER"
						:position="player.position"
						:player-is-ad-mode="playerIsAdMode"
						:event="event"
						:ads="ads"
						:is-couch="isCouch"
					/>
				</template>
			</Player>

			<PlayerControls
				v-if="playerUiConfig.overlays.playerControls.position === 'below'"
				class="player-wrapper__player-controls"
				v-bind="playerControlsProps"
				v-on="playerControlsEvents"
			/>

			<ChromeCastOverlay
				v-if="isCastingOnChromecast"
				@handleChromecastStop="handleChromecastStop"
				@seek="handleChromecastSeek"
			/>
		</div>
	</div>
</template>

<script>

import { mapActions, mapGetters } from 'vuex';

import PlayerOverlays from '@components/player/PlayerOverlays.vue';

import MonetizationImageRenderer from '@components/player/monetization/ImageRenderer.vue';
import MonetizationTakeoverRenderer from '@components/player/monetization/MonetizationTakeoverRenderer.vue';
import Player from '@components/player/Player.vue';
import ChromeCastOverlay from '@components/player/ChromeCastOverlay.vue';
import { PLAYER_STATE } from '@components/player/enums';
import PlayerControls from '@shared/components/PlayerControls/PlayerControls.vue';
import { EVENT, bus, playerBus } from '@videotools/js/events';
import { PLAYER_SPEED_OPTIONS } from '@videotools/js/constants/player';
import { processHotkeys, events as HOTKEY_EVENT } from '@videotools/js/events/modules/player-hotkeys';
import { APP_TYPE_TAGGING_TOOL, APP_TYPE_VIEW_MODE, APP_TYPE_COUCH_MODE } from '@videotools/js/constants/app';
import storage from '@shared/js/storage';
import { safeSessionStorage } from '@shared/js/safeWebStorage';
import { EVENT as CHROMECAST_EVENT } from '@videotools/js/util/Chromecast';
import laroute from '@laroute';
import AdsMixin from '@shared/js/mixins/AdsMixin';
// import ClipsPreviewMixin from '@components/player/overlays/ClipsPreviewMixin';
import { getPlayerControlsProps } from '@shared/components/PlayerControls/getPlayerControlsProps';
import { AdType } from '@shared/enums/AdType';
import { EventVODStatus } from '@shared/enums/EventVODStatus';
import Hls from 'hls.js';

const LIVE_LOOKING_BACK_DELTA = 60;
const SEEK_TO_LIVE_DELTA = 10;
const ERROR_REGISTRATION_DELAY_SECONDS = 3;
const DOUBLE_CLICK_SEEK_INTERVAL = 1000;
const PLAYER_ERROR_TOAST_DELAY = 6000;

const events = [
	EVENT.PLAYER.REQUEST.PLAY,
	EVENT.PLAYER.REQUEST.PAUSE,
	EVENT.PLAYER.REQUEST.SEEK,
	EVENT.PLAYER.REQUEST.SEEK_INTERVAL,
	EVENT.PLAYER.REQUEST.SEEK_TO_LIVE,
	EVENT.PLAYER.REQUEST.SEEK_TAG,
	EVENT.PLAYER.REQUEST.SEEK_NEXT_TAG,
	EVENT.PLAYER.REQUEST.SEEK_PREVIOUS_TAG,
	EVENT.PLAYER.REQUEST.SEEK_LAST_TAG,
	EVENT.PLAYER.REQUEST.SNAP,
	EVENT.PLAYER.REQUEST.SET_QUALITY,
	EVENT.PLAYER.REQUEST.SET_VOLUME,
	EVENT.PLAYER.REQUEST.MUTE,
	EVENT.PLAYER.REQUEST.UNMUTE,
	EVENT.PLAYER.ON.PLAY,
	EVENT.PLAYER.ON.ACCESS_DENIED,
	EVENT.RESET_OVERLAY_TIMEOUT,

	// Couch events
	EVENT.PLAYER.ON.PROGRESS,
	EVENT.COUCH_SEEK_BACKWARDS,
	EVENT.COUCH_SEEK_FORWARDS,
	EVENT.TAG_PLAY,
	EVENT.PLAYER.ON.PAUSE,

	// Hotkey events
	HOTKEY_EVENT.HOTKEY_TOGGLE_PLAY,
	HOTKEY_EVENT.HOTKEY_TOGGLE_OVERLAY,
	HOTKEY_EVENT.HOTKEY_SHOW_OVERLAY,
	HOTKEY_EVENT.HOTKEY_SPEED_UP,
	HOTKEY_EVENT.HOTKEY_SPEED_DOWN,
	HOTKEY_EVENT.HOTKEY_SEEK_BACKWARDS_TEN_SEC,
	HOTKEY_EVENT.HOTKEY_SEEK_FORWARDS_TEN_SEC,
	HOTKEY_EVENT.HOTKEY_SEEK_BACKWARDS_ONE_SEC,
	HOTKEY_EVENT.HOTKEY_SEEK_FORWARDS_ONE_SEC,
];

export default {
	name:       'PlayerWrapper',
	components: {
		PlayerOverlays,
		MonetizationImageRenderer,
		MonetizationTakeoverRenderer,
		ChromeCastOverlay,
		Player,
		PlayerControls,
	},
	mixins: [
		AdsMixin,
		// ClipsPreviewMixin
	],
	props: {
		autoplay: {
			type:    Boolean,
			default: false,
		},
	},
	data() {
		return {
			isWaitingForLive:            false,
			scrubberDragging:            false,
			waitingForLiveTimeout:       -1,
			position:                    0,
			// playFromEventStart (see Vorlaufzeit): set when entering via event card "From Event Start"
			// only in live events
			playFromEventStart:          window.location.search.includes('fromEventStart'),
			mediaDuration:               0,
			errorTimeout:                -1,
			playerWidth:                 null,
			playerHasLoadedMedia:        false,
			initialPlayRegistered:       false,
			isCastingOnChromecast:       false,
			lastChromecastPosition:      0,
			resumingFromChromecast:      false,
			initialPlayerPosition:       null,
			corePlayerReady:             false,
			eventExtrasLoaded:           false,
			isStorybook:                 window.isStorybook,
			backUrl:                     window.arenaPlayerEmbedConfig?.backUrl,
			previewDurationExceeded:     false,
			previewStarted:              false,
			vastAdLink:                  null,
			isInteractionOverlayVisible: false,
			showDisabledAutoplayOverlay: false,
			// couch data
			sortedCouchTags:             [],
			currentCouchTagIndex:        -1,
			couchElapsed:                0,
			doubleClickTimer:            null,
			playerResizeObserver:        null,
			takeoverAd:                  null,
			takeoverStageRect:           null,

			// player error toast
			playerErrorToastDisabled:          false,
			shouldShowStreamEndsEarlySnackbar: false,
			lastPlayerError:                   null,
			playerErrorTimeout:                null,

			// orientation change detection
			previousOrientation:       null,
			orientationChangeListener: null,

			// constants
			PLAYER_STATE,
			AdType,
		};
	},
	computed: {
		...mapGetters({
			isCouch:                 'app/isInCouchMode',
			isCinemaMode:            'app/isCinemaMode',
			initialPreviewDuration:  'app/initialPreviewDuration',
			isEmbedded:              'app/isEmbedded',
			isMobileWebAppPortrait:  'app/isMobileWebAppPortrait',
			isMobileWebApp:          'app/isMobileWebApp',
			isTaggingToolMode:       'app/isTaggingToolMode',
			isSidebarOpen:           'app/isSidebarOpen',
			isFullscreen:            'device/fullscreen',
			isMobile:                'device/isMobile',
			isMobilePortrait:        'device/isMobilePortrait',
			isMobileLandscape:       'device/isMobileLandscape',
			isMobileAppleDevice:     'device/isMobileAppleDevice',
			isTouchDevice:           'device/isTouchDevice',
			isAppleDevice:           'device/isAppleDevice',
			isLandscape:             'device/isLandscape',
			isPortrait:              'device/isPortrait',
			event:                   'event/event',
			ads:                     'event/ads',
			customOverlayImage:      'event/customOverlayImage',
			isLive:                  'event/isLive',
			isUpcoming:              'event/isUpcoming',
			streamURL:               'event/streamURL',
			tags:                    'event/tags',
			couchTagsFromStore:      'event/couchTags',
			lineup:                  'event/lineup',
			sportName:               'event/sportName',
			timelineTags:            'event/timelineTags',
			streamLeadTimeSeconds:   'event/streamLeadTimeSeconds',
			streamLagTimeSeconds:    'event/streamLagTimeSeconds',
			showScoreboardScore:     'event/showScoreboardScore',
			showScoreboardTime:      'event/showScoreboardTime',
			customText:              'event/customText',
			customTextEnabled:       'event/customTextEnabled',
			matchdayEventChanged:    'event/matchdayEventChanged',
			password:                'event/password',
			eventURL:                'event/eventURL',
			hasTimelineItems:        'event/hasTimelineItems',
			playerError:             'error/playerError',
			autostartCouchMode:      'features/autostartCouchMode',
			forceOverlayVisible:     'features/forceOverlayVisible',
			hasMinimalTimeline:      'features/hasMinimalTimeline',
			isTimelineEnabled:       'features/isTimelineEnabled',
			isPlayerTimeEnabled:     'features/isPlayerTimeEnabled',
			clickInteractionEnabled: 'features/clickInteractionEnabled',
			keyInteractionEnabled:   'features/keyInteractionEnabled',
			loop:                    'features/loop',
			forceMuted:              'features/forceMuted',
			isLoopBackgroundMode:    'features/isLoopBackgroundMode',
			isFakeDVR:               'player/isFakeDVR',
			volumeLevel:             'player/volumeLevel',
			isMuted:                 'player/isMuted',
			audioTracks:             'player/audioTracks',
			chromecast:              'player/chromecast',
			wasMutedManually:        'player/wasMutedManually',
			googleImaIsActive:       'player/googleImaIsActive',
			playStatus:              'player/playStatus',
			playerIsAdMode:          'player/isAdMode',
			playerIsAdModeIdle:      'player/isAdModeIdle',
			playerQualityLevels:     'player/playerQualityLevels',
			airPlay:                 'player/airPlay',
			currentSpeed:            'player/playSpeed',
			currentAudioTrackId:     'player/currentAudioTrackId',
			playerUiConfig:          'playerUi/uiConfig',
			clipIsLiked:             'clipLikes/isLiked',
		}),
		adsDisabled() {
			const isDebugMode = new URLSearchParams(window.location.search).has('debug');
			const [navEntry] = performance?.getEntriesByType('navigation');
			const wasReloaded = navEntry?.type === 'reload';
			const hasSeenAd = safeSessionStorage.getItem(`adPlayed_${this.event.id}`) === 'true';

			if ((this.matchdayEventChanged || (wasReloaded && hasSeenAd) || isDebugMode) && !window.isStorybook) {
				return true;
			}
			return false;
		},
		isConsideredLive() {
			return !this.isFakeDVR && this.isLive;
		},
		forcedDuration() {
			// Cap duration at event duration for live events, as long as the VOD is not available yet
			// Rendered VOD's are always trimmed to Event duration, so we don't need to cap the duration
			if (this.event && !this.event.vodOnly && this.event.vodStatus !== EventVODStatus.AVAILABLE && Number.isFinite(this.event.durationSeconds)) {
				return this.event.durationSeconds - this.streamLagTimeSeconds;
			}
			return undefined;
		},
		isLookingBackInLiveEvent() {
			return this.isLive && this.position < this.mediaDuration - LIVE_LOOKING_BACK_DELTA - this.streamLeadTimeSeconds;
		},
		hasTakeoverAd() {
			return this.ads?.some(ad => ad.type === AdType.TAKEOVER_AD);
		},
		vastURL() {
			if (this.isTaggingToolMode || this.isManager) return null;
			const vastAd = this.ads?.find((ad) => ad.type === 'player_ad');

			if (vastAd && vastAd.vastXmlUrl) {
				const vastUrl = vastAd.vastXmlUrl;
				if (new URL(vastUrl).host === window.location.host) {
					return this.appendAdAttributes(vastUrl);
				} else if (vastAd.source === 'adserver' && vastAd.adserverType === 'adbutler') {
					return this.appendAdbutlerAttributes(vastAd, vastUrl);
				} else {
					return vastUrl;
				}
			}
			return null;
		},
		prerollAdLink() {
			if (this.isTaggingToolMode || this.isManager) return null;
			// ad link is prioritized over the vastAdLink because it's set in the manager
			return this.ads.find((ad) => ad.type === 'player_ad')?.link ?? this.vastAdLink ?? null;
		},
		getAppType() {
			if (this.isTaggingToolMode) {
				return APP_TYPE_TAGGING_TOOL;
			} else if (this.isCouch) {
				return APP_TYPE_COUCH_MODE;
			}
			return APP_TYPE_VIEW_MODE;
		},
		hotkeysDisabled() {
			return this.playerIsAdMode;
		},
		isManager() {
			return window.managerDomain && document.location.host === window.managerDomain;
		},
		previewLimitReached() {
			return this.previewDurationExceeded;
		},
		forcePlayVisible() {
			return [PLAYER_STATE.AD_IDLE, PLAYER_STATE.AD_PAUSED].includes(this.playStatus);
		},
		playerControlsProps() {
			return getPlayerControlsProps({
				playerControlsUiConfig:   this.playerUiConfig.overlays.playerControls,
				airPlayConfig:            this.airPlay,
				isLookingBackInLiveEvent: this.isLookingBackInLiveEvent,
				isConsideredLive:         this.isConsideredLive,
				playerIsAdMode:           this.playerIsAdMode,
				playerIsAdModeIdle:       this.playerIsAdModeIdle,
				eventDurationSeconds:     this.event?.durationSeconds,
				duration:                 this.player?.duration,
				mediaDuration:            this.mediaDuration - this.streamLeadTimeSeconds,
				position:                 this.player?.position,
				adDuration:               this.player?.adDuration,
				adPosition:               this.player?.adPosition,
				currentCouchTagDuration:  this.currentCouchTagDuration,
				currentCouchTagElapsed:   this.currentCouchTagElapsed,
				isPlaying:                this.player?.state === PLAYER_STATE.PLAYING || this.player?.state === PLAYER_STATE.AD_PLAYING,
				isCouch:                  this.isCouch,
				isFullscreen:             this.isFullscreen,
				isTouchDevice:            this.isTouchDevice,
				isMobileWebApp:           this.isMobileWebApp,
				isEmbedded:               this.isEmbedded,
				isCinemaMode:             this.isCinemaMode,
				playerSpeedOptions:       PLAYER_SPEED_OPTIONS,
				currentSpeed:             this.currentSpeed,
				currentCouchTagNumber:    this.currentCouchTagNumber,
				couchTagsCount:           this.couchTagsCount,
				currentCouchTag:          this.currentCouchTag,
				currentCouchTagLikes:     this.currentCouchTag ? this.currentCouchTag.likes : 0,
				isLikedByUser:            this.isLikedByUser,
				audioTracks:              this.audioTracks,
				currentAudioTrackId:      this.currentAudioTrackId,
				playerQualityLevels:      this.playerQualityLevels,
				volumeLevel:              this.volumeLevel,
				isMuted:                  this.isMuted,
				scrubberDragging:         this.scrubberDragging,
				storedQualityLevelId:     storage.videoQuality,
				showBorder:               false,
				isSidebarOpen:            this.isSidebarOpen,
			});
		},
		playerControlsEvents() {
			return {
				play:  this.handlePlayerPlay,
				pause: this.handlePlayerPause,
				seek:  (time) => {
					playerBus.seek(time);
				},
				'set-mute': (muted) => {
					this.setMute(muted);
					this.setWasMutedManually(muted);
				},
				'set-volume':      this.setVolumeLevel,
				'back-to-live':    this.seekToLive,
				'set-audio-track': (id) => {
					this.player.setAudioTrack(id);
				},
				'cinema-click': () => {
					this.setCinemaMode(!this.isCinemaMode);
				},
				'chromecast-click': (data) => {
					this.handleChromecastCast(data);
				},
				'fullscreen-click':         this.toggleFullscreen,
				'picture-in-picture-click': this.togglePictureInPicture,
				'set-speed':                (speed) => {
					this.player.setSpeed(speed);
				},
				'settings-opened': () => {
					bus.publish(EVENT.RESET_OVERLAY_TIMEOUT);
				},
				'settings-still-open': () => {
					bus.publish(EVENT.RESET_OVERLAY_TIMEOUT);
				},
				'audio-tracks-opened': () => {
					bus.publish(EVENT.RESET_OVERLAY_TIMEOUT);
				},
				'audio-tracks-still-open': () => {
					bus.publish(EVENT.RESET_OVERLAY_TIMEOUT);
				},
				'set-quality': (index) => {
					this.player.setQuality(index);
				},
				'cursor-drag-start': () => {
					this.setScrubberDragging(true);
				},
				'cursor-drag-end': () => {
					this.setScrubberDragging(false);
				},
				// couch specific events
				'prev-clip': this.handlePrevCouchClip,
				'next-clip': this.handleNextCouchClip,
				replay:      this.replayCouchClip,
				like:        this.handleLike,
				share:       () => {
					if (this.isCouch) {
						this.handleShareClip(this.currentCouchTag);
					} else {
						this.handleShare();
					}
				},
				'toggle-sidebar': () => {
					this.setSidebarOpen(!this.isSidebarOpen);
				},
			};
		},
		// couch specific computed props
		currentGraphicsConfiguration() {
			if (!this.currentCouchTag) return null;

			const eventName = this.transTagLabel(this.currentCouchTag.name, this.sportName, this.currentCouchTag.tag?.type);

			return {
				eventName,
				playerName:   this.currentCouchTag.player,
				playerNumber: this.currentCouchTag.playerNumber,
				playerImage:  this.currentCouchTag.playerLogo,
				teamLogo:     this.currentCouchTag.teamLogo,
			};
		},
		couchTags() {
			const playerNumberMap = {};
			if (this.lineup?.length) {
				for (const item of this.lineup) {
					playerNumberMap[item.player.id] = item.number;
				}
			}

			return this.sortedCouchTags
				.filter((tag) => tag.duration > 0)
				.map((t) => {
					const { id, player, team, timestamp, name, secondsAfter, secondsBefore, likes } = t;
					if (player) player.number = playerNumberMap[player.id];

					return {
						id,
						key:          id,
						name,
						time:         timestamp,
						start:        timestamp - secondsBefore,
						end:          timestamp + secondsAfter,
						secondsBefore,
						secondsAfter,
						player:       player?.name,
						playerNumber: player?.number,
						playerLogo:   player?.imageUrl,
						participant:  team?.clubTeamName,
						teamLogo:     team?.club?.imageUrl,
						likes,
						url:          t.url,
						imageUrl:     t.imageUrl,
						shareLinks:   t.shareLinks,
						type:         t.tag?.type,
					};
				});
		},
		couchTagsCount() {
			return this.couchTags.length;
		},
		currentCouchTag() {
			if (this.currentCouchTagIndex >= 0) {
				return this.couchTags[this.currentCouchTagIndex];
			}
			return null;
		},
		currentCouchTagNumber() {
			return this.currentCouchTagIndex + 1;
		},
		isLikedByUser() {
			return this.clipIsLiked(this.event?.id, this.currentCouchTag?.id);
		},
		currentCouchTagDuration() {
			if (!this.currentCouchTag) return 0;
			return this.currentCouchTag.end - this.currentCouchTag.start;
		},
		currentCouchTagElapsed() {
			if (!this.currentCouchTag) return 0;
			return this.position - this.currentCouchTag.start;
		},
		// todo controls2 clips preview mobile landscape
		// clipsPreviewTags() {
		// 	return this.tags.map(e => {
		// 		const time = e.timestamp;
		// 		const participant =
		// 			(e.participant && e.participant.id) ||
		// 			(e.team && e.team.id) ||
		// 			(e.player && e.player.id);

		// 		return {
		// 			id:    e.id,
		// 			name:  e.name,
		// 			image: e.imageUrl,
		// 			participant,
		// 			time,
		// 			tag:   e,
		// 		};
		// 	});
		// },
		shouldShowPlayerErrorToast() {
			const error = this.lastPlayerError;
			if (!error) return;

			return !this.playerErrorToastDisabled && error.type === Hls.ErrorTypes.NETWORK_ERROR &&
				(
					(error.details === Hls.ErrorDetails.MANIFEST_LOAD_ERROR && error.response?.code === 0) ||
					[Hls.ErrorDetails.LEVEL_LOAD_ERROR, Hls.ErrorDetails.AUDIO_TRACK_LOAD_ERROR].includes(error.details)
				);
		},
	},
	watch: {
		event() {
			this.setFakeDVR(false);
			this.initialPlayerPosition = null;
		},
		isFullscreen() {
			this.recomputePlayerWidth();
		},
		matchdayEventChanged() {
			if (this.player.state === PLAYER_STATE.AD_PLAYING) {
				this.player.skipAd();
			}
		},
		volumeLevel(newVolumeLevel) {
			this.player.setVolume(newVolumeLevel);
		},
		isMuted(isMuted) {
			if (isMuted) {
				this.player?.mute();
			} else {
				this.player?.unmute();
			}
		},
		streamURL() {
			if (window.isStorybook) {
				this.$nextTick(() => {
					this.init();
				});
			}
		},
		// couch specific watch
		currentCouchTag() {
			this.$refs.playerOverlays?.handleGameEventOverlayReset();
		},
		couchTagsFromStore() {
			this.prepareCouchMode();
		},
		playerError(error) {
			if (!error) {
				this.lastPlayerError = error;
				if (this.playerErrorTimeout) {
					clearTimeout(this.playerErrorTimeout);
					this.playerErrorTimeout = null;
				}
			} else if (error.type === Hls.ErrorTypes.NETWORK_ERROR) {
				if (!this.playerErrorTimeout) {
					this.playerErrorTimeout = setTimeout(
						() => this.lastPlayerError = error,
						PLAYER_ERROR_TOAST_DELAY
					);
				}
			}

			if (!this.lastPlayerError) {
				this.playerErrorToastDisabled = false;
			}
		},
		isCouch(value) {
			if (value) {
				// prepare player
				if (this.couchTags.length > 0) {
					const firstTag = this.couchTags[0];
					const start = firstTag.time - firstTag.secondsBefore;
					playerBus.seek(start);
					playerBus.play();
				}
			}
		},
		scrubberDragging(value) {
			if (value) {
				this.$refs.playerOverlays?.clearAutoHidePlayerControlsTimeout();
			} else {
				setTimeout(() => {
					if (this.player.state === PLAYER_STATE.PLAYING) {
						this.$refs.playerOverlays?.autoHidePlayerControls();
					}
				}, 100);
			}
		},
	},
	beforeMount() {
		this.checkForTvRedirect();
	},
	updated() {
		if (!this.$refs.player) return;
		if (this.$refs.player !== this.player) {
			this.player = this.$refs.player;
			playerBus.setPlayer(this.player, {
				getDuration: () => this.player.getDuration(),
				getPosition: () => this.player.getPosition(),
			});
		}
	},
	mounted() {
		this.init();
		this.prepareCouchMode();
		if (this.playFromEventStart) {
			window.history.replaceState({}, '', window.location.pathname);
		}
		this.$nextTick(() => {
			this.setInitialPlayerPosition();
		});

		// Initialize orientation change detection on mobile
		if (this.isMobileWebApp) {
			this.setupOrientationChangeDetection();
		}
	},
	destroyed() {
		// unsubscribe from bus events
		for (const event of events) {
			bus.unsubscribe(event, this[`BUS-${event}`]);
		}

		// Hotkeys
		if (!this.isMobileWebApp) {
			document.removeEventListener('keydown', this.processHotkeys);
		}

		// clear timeouts
		clearTimeout(this.waitingForLiveTimeout);

		window.removeEventListener('resize', this.recomputePlayerWidth, true);

		if (this.playerResizeObserver) {
			this.playerResizeObserver.disconnect();
		}

		// Remove orientation change listener
		if (this.orientationChangeListener) {
			window.removeEventListener('orientationchange', this.orientationChangeListener);
		}
	},
	methods: {
		...mapActions({
			enableCouchMode:                'app/enableCouchMode',
			disableCouchMode:               'app/disableCouchMode',
			setCinemaMode:                  'app/setCinemaMode',
			setSidebarOpen:                 'app/setSidebarOpen',
			editEventTag:                   'eventTagEdit/editEventTag',
			loadEventExtra:                 'event/loadEventExtra',
			setPreviewLimitReached:         'event/setPreviewLimitReached',
			disableAutostartCouchMode:      'features/disableAutostartCouchMode',
			setFakeDVR:                     'player/setFakeDVR',
			setPlaySpeed:                   'player/setPlaySpeed',
			setPlayStatus:                  'player/setPlayStatus',
			setQualityLevels:               'player/setQualityLevels',
			setAirPlay:                     'player/setAirPlay',
			setAudioTracks:                 'player/setAudioTracks',
			setCurrentAudioTrackId:         'player/setCurrentAudioTrackId',
			setIsPictureInPictureAvailable: 'player/setIsPictureInPictureAvailable',
			setVolumeLevel:                 'player/setVolumeLevel',
			setMute:                        'player/setMute',
			setWasMutedManually:            'player/setWasMutedManually',
			setGoogleImaActive:             'player/setGoogleImaActive',
			setAutoplayMuted:               'player/setAutoplayMuted',
			setPlayerError:                 'error/setPlayerError',
			likeClip:                       'clipLikes/likeClip',
		}),
		checkForTvRedirect() {
			// After login or payment, users land here on TV. But we want them to go to the TV page.
			if ($Helpers.isBlueTv() && !this.isUpcoming) {
				const tvRoute = laroute.route('arena.event_tv', {
					event: this.event.id,
					slug:  $Helpers.slugify(this.event.nameTranslations?.[window.$arenaConfig.defaultLanguage] || this.event.name),
				});

				window.location.href = tvRoute;
			}
		},
		init() {
			if (!this.$refs.player) return;

			if (!this.autoplay) {
				this.showDisabledAutoplayOverlay = true;
				this.$refs.playerOverlays?.showInteractionOverlay();
			}
			this.player = this.$refs.player;
			playerBus.setPlayer(this.player, {
				getDuration: () => this.player.getDuration(),
				getPosition: () => this.player.getPosition(),
			});

			// wait for live before updating elapsed
			const millisecondsUntilLive = (Number(this.event.startTimestamp) * 1000) - Date.now();
			this.isWaitingForLive = millisecondsUntilLive > 0;
			if (this.isWaitingForLive) {
				this.waitingForLiveTimeout = setTimeout(() => {
					this.isWaitingForLive = false;
				}, millisecondsUntilLive);
			}

			// subscribe to bus events
			for (const event of events) {
				bus.subscribe(event, this[`BUS-${event}`]);
			}

			// Register hotkeys
			if (!this.isMobileWebApp) {
				this.registerHotkeys();
			}

			// monetization
			if (!this.vastURL) {
				bus.publish(EVENT.PLAYER.ON.MONETIZATION_AD_START);

				if (this.autostartCouchMode) {
					this.disableAutostartCouchMode();
					this.enableCouchMode();
				}
			}
			// trigger overlay ads manually if preroll ad is disabled which otherwise would be triggered by the player
			if (this.vastURL && this.adsDisabled) {
				bus.publish(EVENT.PLAYER.ON.MONETIZATION_AD_START);
			}

			this.recomputePlayerWidth();

			if (window.ResizeObserver && this?.$refs['player-wrapper-container']) {
				const observer = new ResizeObserver(this.recomputePlayerWidth);
				observer.observe(this?.$refs['player-wrapper-container']);
				this.playerResizeObserver = observer;
			} else {
				window.addEventListener('resize', () => this.recomputePlayerWidth, true);
			}

			this.detectStreamExceedingEventDuration();

			if (!window.isStorybook) {
				this.handleChromecastEvents();
			}
		},
		recomputePlayerWidth() {
			this.$nextTick(() => {
				setTimeout(() => {
					requestAnimationFrame(() => {
						this.playerWidth = this?.$refs['player-wrapper-container']?.clientWidth;
					});
				}, 50);
			});
		},
		handleTakeoverAdStateChange(player, state) {
			this.takeoverAd = state;
			player?.setTakeoverAdState(state);
		},
		handleTakeoverStageRectChange(rect) {
			this.takeoverStageRect = rect;
		},
		toggleFullscreen() {
			if (this.playerUiConfig.overlays.iosRotateInfo.enabled) {
				this.$refs.playerOverlays?.showIosRotateInfo();
				return;
			}
			this.player.toggleFullscreen();
		},
		togglePictureInPicture() {
			this.player.togglePictureInPicture();
		},
		handleShareClip(clip) {
			if (!clip) return;
			bus.publish(EVENT.SHARE_CLIP, clip);
		},
		handleOverlayClick() {
			if (this.playerIsAdMode && this.prerollAdLink) {
				if (this.player.state === PLAYER_STATE.AD_PLAYING) {
					window.open(this.prerollAdLink, '_blank');
				} else {
					this.handlePlayerPlay();
				}
			} else {
				this.handlePlayerPlayToggle();
			}
		},
		handleTouchTogglePlayPause() {
			if (this.player.state === PLAYER_STATE.PLAYING) {
				this.handlePlayerPause();
				this.$refs.playerOverlays?.handlePause();
				this.$refs.playerOverlays?.clearAutoHidePlayerControlsTimeout();
			} else {
				this.handlePlayerPlay();
				this.$refs.playerOverlays?.handlePlay();
				this.$refs.playerOverlays?.autoHidePlayerControls();
			}
		},
		// tab handlers only for touch devices of all sizes
		handleSingleTap() {
			if (this.playerIsAdMode && this.prerollAdLink) {
				if (this.player.state === PLAYER_STATE.AD_PLAYING) {
					window.open(this.prerollAdLink, '_blank');
				} else {
					this.handlePlayerPlay();
				}
			} else {
				if (!this.isInteractionOverlayVisible) {
					this.$refs.playerOverlays?.showInteractionOverlay();
					if (this.player.state === PLAYER_STATE.PLAYING) {
						this.$refs.playerOverlays?.autoHidePlayerControls();
					}
				} else {
					this.$refs.playerOverlays?.hideInteractionOverlay();
				}
			}
		},
		handleDoubleTap(dir) {
			this.handleMultiTap(2, dir);
			this.$refs.playerOverlays?.autoHidePlayerControls();
		},
		handleTripleTap(dir) {
			this.handleMultiTap(3, dir);
			this.$refs.playerOverlays?.autoHidePlayerControls();
		},
		handleMultiTap(tapCount, dir = 'forward') {
			const amount = tapCount > 2 ? 20 : 10;

			// skip backward
			if (dir === 'backward') {
				this.handleBackward(amount);
				this.$refs.playerOverlays?.handleBackward(amount);
			}

			// skip forward
			if (dir === 'forward') {
				if (!this.canSkipForward) return;
				this.handleForward(amount);
				this.$refs.playerOverlays?.handleForward(amount);
			}
		},
		setScrubberDragging(value) {
			this.scrubberDragging = value;
		},
		handleStateUpdate(value) {
			this.setPlayStatus(value);
		},
		handlePositionUpdate(value) {
			this.position = value;
			playerBus.emitProgress(value);
			this.detectStreamExceedingEventDuration();
		},
		/**
		 * Detects when a live stream has exceeded the event's scheduled duration.
		 * This can happen when the stream continues beyond the manually set event end time.
		 * When detected, we mark it as "fake DVR" (not a real live stream anymore).
		 *
		 * Conditions that trigger this:
		 * 1. Stream duration exceeds event duration (stream is longer than event was scheduled)
		 * 2. Current time has passed the event end timestamp (event has ended but stream continues)
		 *
		 * Only seeks to beginning if user is near the end of the event to avoid interrupting playback
		 * when user is watching in the middle of the event.
		 */
		detectStreamExceedingEventDuration() {
			// Skip check for VOD-only events or if already detected
			if (!this.event || this.event.vodOnly || this.isFakeDVR) {
				return;
			}

			const currentTime = Date.now() / 1000;
			const streamDurationExceedsEvent = Math.abs(this.mediaDuration) - this.streamLeadTimeSeconds > this.event.duration;
			const eventHasEnded = currentTime >= this.event.endTimestamp;

			if (streamDurationExceedsEvent || eventHasEnded) {
				// Stream has exceeded event duration - mark as fake DVR
				this.setFakeDVR(true);

				// Only seek to beginning if user is near the end of the event
				// This prevents interrupting users who are watching in the middle of the event
				// Use player duration (accounts for forcedDuration) to check if user is near end
				const playerDuration = this.player?.duration ?? this.event.durationSeconds;
				const isNearEventEnd = this.position >= playerDuration - LIVE_LOOKING_BACK_DELTA;

				if (isNearEventEnd) {
					this.player.seek(0);
				}
			}
		},
		handleDurationUpdate(value) {
			this.$emit('change-duration', value);
			bus.publish(EVENT.PLAYER.ON.DURATION, value);
		},
		handleMediaDurationUpdate(value) {
			this.mediaDuration = value;
		},
		handleQualityLevelsUpdate(value) {
			this.setQualityLevels(value);

			// restore preferred quality from localStorage
			if (storage.videoQuality && storage.videoQuality !== this.player.currentQualityLevelIndex) {
				this.player.setQuality(storage.videoQuality);
			}
		},
		handleSpeedUpdate(value) {
			this.setPlaySpeed(value);
		},
		handleShare() {
			if (this.isFullscreen) {
				bus.publish(EVENT.FULLSCREEN_TOGGLE);
			}
			bus.publish(EVENT.SHARE_EVENT);
		},
		handleReady() {
			storage.playerRestore = {
				eventId: this.event.id,
			};
			playerBus.emitReady();

			if (this.previewDurationExceeded) {
				this.player.destroy();
			}
		},
		setInitialPlayerPosition() {
			if (this.playFromEventStart) {
				// If the stream started too late, seek to a few seconds after stream start to skip possible black frames
				const initialDelay = this.streamLagTimeSeconds > 0 ? 15 : 0;

				const kickoffHasHappened = this.event.startTimestamp + (this.event.kickoffOffsetMinutes * 60) < Date.now() / 1000;
				if (kickoffHasHappened) {
					// start from kickoff (minus 10 seconds margin)
					// In Live streams, seekable range doesn't include 0, so we start at 0.4 seconds
					this.initialPlayerPosition = Math.max(0.4, (this.event.kickoffOffsetMinutes * 60) - 10);
				} else {
					// start from beginning
					// In Live streams, seekable range doesn't include 0, so we start at 0.4 seconds
					this.initialPlayerPosition = 0.4 + initialDelay;
				}
				return;
			}
			if (!this.isLive && !this.autostartCouchMode && storage.playerRestore && storage.playerRestore.eventId === this.event.id && storage.playerRestore.position) {
				this.initialPlayerPosition = storage.playerRestore.position;
			}
		},
		handleBackward(seconds = 10) {
			if (this.isCouch) {
				bus.publish(EVENT.COUCH_SEEK_BACKWARDS);
			} else {
				this.player.seek(Math.max(0, this.position - seconds));
			}
		},
		handleForward(seconds = 10) {
			if (this.isCouch) {
				bus.publish(EVENT.COUCH_SEEK_FORWARDS);
			} else {
				this.player.seek(Math.min(this.player.duration, this.player.position + seconds));
			}
		},
		canSkipForward() {
			const isLivePlaying = this.isLive && !this.isLookingBackInLiveEvent;
			return !this.playerIsAdMode && !this.isCouch && !isLivePlaying;
		},
		registerHotkeys() {
			if (!this.keyInteractionEnabled) {
				return;
			}
			document.addEventListener('keydown', this.processHotkeys);
		},
		processHotkeys(event) {
			processHotkeys(event, this.getAppType, this.hotkeysDisabled);
		},
		handleError(error) {
			if (error?.type !== this.lastPlayerError?.type) {
				clearTimeout(this.errorTimeout);
				this.errorTimeout = setTimeout(() => {
					this.setPlayerError(error);
				}, ERROR_REGISTRATION_DELAY_SECONDS * 1000);
			}

			if ([Hls.ErrorTypes.NETWORK_ERROR].includes(error.type) && error.isFatal && window.Sentry) {
				Sentry.captureMessage('Player: HLS Error: ' + error.type + ' (' + error.details + ')', {
					level: 'warning',
					tags:  {
						eventId: this.event.id,
					},
				});
			}

			// hls marks as fatal if it can't recover, e.g. max level loading retries exhausted
			if (error.isFatal) {
				this.playerErrorToastDisabled = true;
				return;
			}

			this.lastPlayerError = error;
		},
		handleErrorClear() {
			clearTimeout(this.errorTimeout);
			this.setPlayerError(null);
		},
		handleAdPlay() {
			bus.publish(EVENT.PLAYER.ON.AD_MODE_ENTER);
			this.trackPlayerBehaviour('Ad Playing');
		},
		handleAdTime({ duration, position }) {
			bus.publish(EVENT.PLAYER.ON.AD_TIME, { duration, position });
		},
		handleAdSkipped(watchedTillEnd = false) {
			bus.publish(EVENT.PLAYER.ON.AD_MODE_EXIT);
			this.triggerMonetizationAd();
			this.trackPlayerBehaviour(watchedTillEnd ? 'Ad Finished' : 'Ad Skipped');
			safeSessionStorage.setItem(`adPlayed_${this.event.id}`, 'true');
		},
		handleFirstFrameLoaded() { // Won't be called if the player is in ad mode
			// Function to preview tag from start to end
			const previewEventTag = (eventTagId) => {
				if (!eventTagId) return;
				// Find tag object
				const tag = this.tags.find((t) => t.id === Number(eventTagId));
				if (tag) playerBus.seekTag(tag);
			};

			// If there is a tag in queryparams url it should redirect the video to it
			let hasTagPreview = false;

			if (this.$route?.query?.eventTagId) {
				const { eventTagId } = this.$route.query;
				previewEventTag(eventTagId);
				hasTagPreview = true;
			} else {
				// Is embeded
				const urlSearchParams = new URLSearchParams(window.location.search);
				const params = Object.fromEntries(urlSearchParams.entries());

				if (params.eventTagId) {
					previewEventTag(params.eventTagId);
					hasTagPreview = true;
				}
			}

			if (!hasTagPreview) {
				// Play from event start, at live position or restore position
				const maxRestorePosition = this.mediaDuration - this.streamLeadTimeSeconds - 10; // 10 seconds margin
				const isAtOrNearEnd = this.initialPlayerPosition >= maxRestorePosition;
				const tooShortToRestore = this.mediaDuration - this.streamLeadTimeSeconds < 300; // 300 seconds = 5 minutes

				if (this.resumingFromChromecast && this.initialPlayerPosition !== null) {
					// Resuming from Chromecast: seek to the last Chromecast position
					this.player.seek(this.initialPlayerPosition);
					this.resumingFromChromecast = false;
				} else if (this.playFromEventStart && this.initialPlayerPosition !== null && this.isLive) {
					this.player.seek(this.initialPlayerPosition);
				} else if (this.isConsideredLive && !this.playFromEventStart) {
					this.seekToLive();
				} else if (storage.playerRestore?.eventId === this.event.id && !this.isLive && !this.autostartCouchMode && !isAtOrNearEnd && !tooShortToRestore) {
					this.player.seek(storage.playerRestore.position);
				}
			}
		},
		seekToLive() {
			this.player.seek(this.player.getInternalDuration() - SEEK_TO_LIVE_DELTA - this.streamLeadTimeSeconds);
		},
		triggerMonetizationAd() {
			// Trigger monetization [next tick because mobile slot components are not recreated]
			// TODO: refactor to use proper events
			// -> EVENT.X.REQUEST.Y to request something
			// -> EVENT.X.ON.Y to react to something
			this.$nextTick(() => {
				bus.publish(EVENT.PLAYER.ON.MONETIZATION_AD_START);
			});
		},
		handleAirplay(payload) {
			this.setAirPlay(payload);
		},
		handlePictureInPictureAvailabilityChange(available) {
			this.setIsPictureInPictureAvailable(available);
		},
		appendAdAttributes(url) {
			let result = this.event ? `${url}/${this.event.id}` : url;

			if (this.event && storage.viewerId) {
				result = `${result}/${storage.viewerId}`;
			}

			return result;
		},
		appendAdbutlerAttributes(ad, url) {
			url += '&_abdk[language]=' + this.adButlerLocale;
			if (ad.adbutlerSendLabels && this.getPageLabels().length > 0) {
				url += '&keywords=' + this.getPageLabels().join(',');
			}
			return url;
		},
		handlePlayerPlay() {
			if (this.showDisabledAutoplayOverlay) {
				this.showDisabledAutoplayOverlay = false;
				this.$refs.playerOverlays?.hideInteractionOverlay();
			}
			this.player.play();
		},
		handlePlayerPause() {
			this.player.pause();
		},
		handlePlayerPlayToggle() {
			if (this.player.state === PLAYER_STATE.PLAYING) {
				this.$refs.playerOverlays?.handlePause();
			} else {
				this.$refs.playerOverlays?.handlePlay();
			}
			this.player.togglePlay();
		},
		handleAudioTracksUpdate(value) {
			this.setAudioTracks(value);
		},
		handleAudioTrackChange(value) {
			this.setCurrentAudioTrackId(value);
		},
		handleInteractionOverlayVisibilityChange(visible) {
			this.isInteractionOverlayVisible = visible;
		},
		trackPlayerBehaviour(eventName) {
			if (this.event) {
				this.$Analytics.trackEvent('Player', eventName, {
					eventId:   this.event.id,
					eventName: this.event.name,
				});
			}
		},
		handleAutoplayedMuted(isMuted) {
			this.setAutoplayMuted(isMuted);
		},
		handleAutoplayedMutedClick() {
			this.setMute(false);
		},
		handleMuted() {
			this.setMute(true);
		},
		handleUnmuted() {
			this.setMute(false);
		},
		handleSetVolume(volume) {
			this.setVolumeLevel(volume);
		},
		seekTag(tag, pauseAfterTag = false) {
			const start = Math.max(0, tag.timestamp - tag.secondsBefore);
			if (this.isCouch || !pauseAfterTag) {
				this.player.seek(start);
			} else {
				const duration = tag.secondsBefore + tag.secondsAfter;
				this.player.seekInterval(start, duration);
			}
		},
		handleCorePlayerReady() {
			this.corePlayerReady = true;
			if (!this.eventExtrasLoaded) {
				this.loadEventExtra({ id: this.event.id, cached: !this.isTaggingToolMode });
				this.eventExtrasLoaded = true;
			}
		},
		handlePrerollAdReady() {
			if (!this.eventExtrasLoaded) {
				this.loadEventExtra({ id: this.event.id, cached: !this.isTaggingToolMode });
				this.eventExtrasLoaded = true;
			}
			safeSessionStorage.removeItem(`adPlayed_${this.event.id}`);
		},
		handleAdLinkChange(link) {
			this.vastAdLink = link;
		},
		handleVideoEndPositionReached(reached) {
			// If stream ends (video end position reached) before event ends,
			// we show an error snackbar that the stream might continue later.
			// If scrubber is dragging, we don't show the snackbar
			const nearEventEndMargin = 60;
			if (reached && this.forcedDuration && this.isLive && !this.scrubberDragging && (this.position + nearEventEndMargin) < this.forcedDuration) {
				this.shouldShowStreamEndsEarlySnackbar = true;
			} else {
				this.shouldShowStreamEndsEarlySnackbar = false;
			}
		},
		handleSetGoogleImaActive(isActive) {
			this.setGoogleImaActive(isActive);
		},
		handleChromecastCast() {
			this.initialPlayerPosition = this.position;

			const audioTrackId = this.player.currentAudioTrackId;
			const position = this.position + this.streamLeadTimeSeconds;

			// Capture live seekable range before destroying player (for DVR/live sync on Chromecast)
			let liveSeekableRange = null;
			if (this.isLive && this.player.videoEl?.seekable?.length > 0) {
				liveSeekableRange = {
					start: this.player.videoEl.seekable.start(0),
					end:   this.player.videoEl.seekable.end(0),
				};
			}

			this.player.destroy();
			this.isCastingOnChromecast = true;

			this.chromecast.setEventId(this.event.id);
			this.chromecast.setEventPassword(this.password);
			this.chromecast.castMedia(this.streamURL, {
				position,
				audioTrackId,
				isLive: this.isLive,
				liveSeekableRange,
			});
		},
		handleChromecastStop() {
			this.chromecast.stopCasting();
			// Set the resume position before the player is recreated
			// Add streamLeadTimeSeconds because lastChromecastPosition is in event time, not stream time
			this.initialPlayerPosition = this.lastChromecastPosition - this.streamLeadTimeSeconds;
			this.resumingFromChromecast = true;
			this.isCastingOnChromecast = false;
		},
		handleChromecastSeek(amount) {
			const newPosition = this.chromecast.position;
			this.chromecast.seek(newPosition + amount);
		},
		handleChromecastEvents() {
			this.chromecast.addEventListener(CHROMECAST_EVENT.CASTING_START, () => {
				this.isCastingOnChromecast = true;
			});
			this.chromecast.addEventListener(CHROMECAST_EVENT.CASTING_STOP, () => {
				this.isCastingOnChromecast = false;
			});
			this.chromecast.addEventListener(CHROMECAST_EVENT.PROGRESS, (position) => {
				this.lastChromecastPosition = position;
				this.handlePositionUpdate(position);
			});
		},
		handlePreviewLimitReached() {
			this.previewDurationExceeded = true;
			if (this.player) {
				this.player.destroy();
			}
			this.setPreviewLimitReached();
		},
		handlePreviewStarted() {
			this.previewStarted = true;
		},
		// couch specific methods
		replayCouchClip() {
			playerBus.seek(this.currentCouchTag.start);
		},
		closeCouchMode() {
			bus.publish(EVENT.COUCH_EXIT);
			this.disableCouchMode();
		},
		prepareCouchMode() {
			// init state
			this.sortedCouchTags = Array.from(this.couchTagsFromStore);
			this.sortedCouchTags.sort((a, b) => a.timestamp - b.timestamp);
			if (this.couchTags.length > 0) {
				this.currentCouchTagIndex = 0;
			} else {
				 this.currentCouchTagIndex = -1;
			}
		},
		handlePrevCouchClip() {
			this[`BUS-${EVENT.COUCH_SEEK_BACKWARDS}`]();
		},
		handleNextCouchClip() {
			this[`BUS-${EVENT.COUCH_SEEK_FORWARDS}`]();
		},
		async handleLike() {
			await this.likeClip({ eventId: this.event.id, clipId: this.currentCouchTag.id });
		},
		cancelErrorToast() {
			this.playerErrorToastDisabled = true;
		},
		// When the user has scrolled slightly on mobile, the "artificial fullscreen" sometimes doesn't work as expected,
		// e.g. Player isn't fully visible. This fixes that.
		setupOrientationChangeDetection() {
			// Initialize previous orientation state
			this.previousOrientation = this.isLandscape;

			// Listen for orientation changes
			this.orientationChangeListener = () => {
				// Use requestAnimationFrame to check orientation after the change starts
				requestAnimationFrame(() => {
					// Trigger scroll to top when rotating from portrait to landscape
					if (this.isLandscape && !this.previousOrientation) {
						this.scrollToTop();
					}
					// Update previous orientation state for next change
					this.previousOrientation = this.isLandscape;
				});
			};

			window.addEventListener('orientationchange', this.orientationChangeListener);
		},
		scrollToTop() {
			// Scroll to top immediately when rotating to landscape on iOS
			window.scrollTo(0, 0);
		},

		// bus handlers
		[`BUS-${EVENT.PLAYER.REQUEST.PLAY}`]() {
			this.handlePlayerPlay();
		},
		[`BUS-${EVENT.PLAYER.REQUEST.PAUSE}`]() {
			this.handlePlayerPause();
		},
		[`BUS-${EVENT.PLAYER.REQUEST.SEEK}`](seconds) {
			this.player.seek(Math.max(seconds, 0));
		},
		[`BUS-${EVENT.PLAYER.REQUEST.SEEK_INTERVAL}`]({ start, duration }) {
			this.player.seekInterval(start, duration);
		},
		[`BUS-${EVENT.PLAYER.REQUEST.SEEK_TAG}`](tag, pauseAfterTag = false) {
			this.seekTag(tag, pauseAfterTag);
			bus.publish(EVENT.TAG_PLAY, tag);
		},
		[`BUS-${EVENT.PLAYER.REQUEST.SEEK_NEXT_TAG}`]() {
			if (this.tags.length === 0) return;
			const sortedTags = [...this.tags];
			sortedTags.sort((a, b) => (a.timestamp - a.secondsBefore < b.timestamp - b.secondsBefore ? -1 : 1));
			const OFFSET = 1; // To avoid replaying the same tag
			const tag = sortedTags.find((currentTag) => currentTag.timestamp >= this.position + currentTag.secondsBefore + OFFSET) || sortedTags[0];
			this.seekTag(tag);
			bus.publish(EVENT.TAG_PLAY, tag);
		},
		[`BUS-${EVENT.PLAYER.REQUEST.SEEK_PREVIOUS_TAG}`]() {
			if (this.tags.length === 0) return;
			const sortedTags = [...this.tags];
			sortedTags.sort((a, b) => (a.timestamp - a.secondsBefore < b.timestamp - b.secondsBefore ? -1 : 1));
			const OFFSET = 1; // To avoid replaying the same tag
			const tag = sortedTags.reverse().find((t) => t.timestamp <= this.position - t.secondsBefore - OFFSET) || sortedTags[sortedTags.length - 1];
			this.seekTag(tag);
			bus.publish(EVENT.TAG_PLAY, tag);
		},
		[`BUS-${EVENT.PLAYER.REQUEST.SEEK_LAST_TAG}`]() {
			if (this.tags.length === 0) return;
			const sortedTags = [...this.tags];
			sortedTags.sort((a, b) => (a.timestamp - a.secondsBefore < b.timestamp - b.secondsBefore ? -1 : 1));
			const tag = sortedTags[sortedTags.length - 1];
			this.seekTag(tag);
			bus.publish(EVENT.TAG_PLAY, tag);
		},
		[`BUS-${EVENT.PLAYER.REQUEST.SEEK_TO_LIVE}`]() {
			this.seekToLive();
		},
		[`BUS-${EVENT.PLAYER.REQUEST.SNAP}`]() {
			if (this.isAppleDevice) return;
			const image = this.player.snap();
			bus.publish(EVENT.PLAYER.ON.SNAP, image);
		},
		[`BUS-${EVENT.PLAYER.REQUEST.SET_QUALITY}`](index) {
			this.player.setQuality(index);
		},
		[`BUS-${EVENT.PLAYER.REQUEST.SET_VOLUME}`](value) {
			this.setVolumeLevel(value);
		},
		[`BUS-${EVENT.PLAYER.REQUEST.MUTE}`]() {
			this.setMute(true);
		},
		[`BUS-${EVENT.PLAYER.REQUEST.UNMUTE}`]() {
			this.setMute(false);
		},
		[`BUS-${EVENT.RESET_OVERLAY_TIMEOUT}`]() {
			this.$refs.playerOverlays?.showInteractionOverlay();
			this.$refs.playerOverlays?.autoHidePlayerControls();
		},
		[`BUS-${EVENT.PLAYER.ON.PLAY}`]() {
			if (this.initialPlayRegistered) return;
			this.initialPlayRegistered = true;
			this.trackPlayerBehaviour('Video Started');

			setTimeout(() => {
				if (this.player.state === PLAYER_STATE.PLAYING) {
					this.trackPlayerBehaviour('15 Seconds Playtime');
				}
			}, 15_000);
		},
		[`BUS-${EVENT.PLAYER.ON.ACCESS_DENIED}`]() {
			if (this.playerIsAdMode) {
				this.player.skipAd();
			} else {
				this.player.stop();
				this.previewDurationExceeded = true;
			}
		},
		// couch events
		[`BUS-${EVENT.PLAYER.ON.PAUSE}`]() {
			this.$refs.playerOverlays?.handleGameEventOverlayPause();
		},
		[`BUS-${EVENT.PLAYER.ON.PROGRESS}`](position) {
			if (!this.isCouch) return;
			const roundedPosition = Math.round(position);
			this.couchElapsed = roundedPosition;
			if (!this.currentCouchTag) return;

			// seek to next tag
			if (roundedPosition > this.currentCouchTag.end) {
				let nextIndex = 0;
				if (this.couchTags.length - 1 > this.currentCouchTagIndex) {
					nextIndex = this.currentCouchTagIndex + 1;
				} else {
					nextIndex = 0;
				}
				this.currentCouchTagIndex = nextIndex;
				playerBus.seek(this.currentCouchTag.start);
			} else if (roundedPosition < this.currentCouchTag.start) {
				// seek to previous tag
				let nextIndex = 0;
				if (this.currentCouchTagIndex > 0) {
					nextIndex = this.currentCouchTagIndex - 1;
				}
				this.currentCouchTagIndex = nextIndex;
				playerBus.seek(this.currentCouchTag.start);
			}
		},
		[`BUS-${EVENT.COUCH_SEEK_BACKWARDS}`]() {
			if (!this.currentCouchTag) return;
			if ((this.doubleClickTimer && this.isMobileWebApp) || !this.isMobileWebApp) {
				clearTimeout(this.doubleClickTimer);
				this.doubleClickTimer = null;
				let tagIndex = 0;
				if (this.currentCouchTagIndex > 0) {
					tagIndex = this.currentCouchTagIndex - 1;
				}
				this.currentCouchTagIndex = tagIndex;
				playerBus.seek(this.currentCouchTag.start);
			} else {
				playerBus.seek(this.currentCouchTag.start);
				this.doubleClickTimer = setTimeout(() => {
					this.doubleClickTimer = null;
				}, DOUBLE_CLICK_SEEK_INTERVAL);
			}
		},
		[`BUS-${EVENT.COUCH_SEEK_FORWARDS}`]() {
			if (!this.currentCouchTag) return;
			let tagIndex = 0;
			if (this.currentCouchTagIndex < this.couchTags.length - 1) {
				tagIndex = this.currentCouchTagIndex + 1;
			}
			this.currentCouchTagIndex = tagIndex;
			playerBus.seek(this.currentCouchTag.start);
		},
		[`BUS-${EVENT.TAG_PLAY}`](tag) {
			if (!this.currentCouchTag) return;
			const index = this.couchTags.findIndex((e) => e.id === tag.id);
			if (index === -1) {
				throw new Error('Unable to find requested tag in couch tags.');
			}
			this.currentCouchTagIndex = index;
		},
		// hotkey events
		[`BUS-${HOTKEY_EVENT.HOTKEY_TOGGLE_PLAY}`]() {
			if ([PLAYER_STATE.PLAYING, PLAYER_STATE.AD_PLAYING].includes(this.player.state)) {
				this.handlePlayerPause();
				this.$refs.playerOverlays?.handlePause();
			} else {
				this.handlePlayerPlay();
				this.$refs.playerOverlays?.handlePlay();
			}
		},
		[`BUS-${HOTKEY_EVENT.HOTKEY_TOGGLE_OVERLAY}`]() {
			this.$refs.playerOverlays?.toggleInteractionOverlay();
		},
		[`BUS-${HOTKEY_EVENT.HOTKEY_SHOW_OVERLAY}`]() {
			this.$refs.playerOverlays?.showInteractionOverlay();
		},
		[`BUS-${HOTKEY_EVENT.HOTKEY_SEEK_BACKWARDS_TEN_SEC}`]() {
			this.$refs.playerOverlays?.handleBackward(10);
		},
		[`BUS-${HOTKEY_EVENT.HOTKEY_SEEK_FORWARDS_TEN_SEC}`]() {
			this.$refs.playerOverlays?.handleForward(10);
		},
		[`BUS-${HOTKEY_EVENT.HOTKEY_SEEK_BACKWARDS_ONE_SEC}`]() {
			this.$refs.playerOverlays?.handleBackward(1);
		},
		[`BUS-${HOTKEY_EVENT.HOTKEY_SEEK_FORWARDS_ONE_SEC}`]() {
			this.$refs.playerOverlays?.handleForward(1);
		},
		[`BUS-${HOTKEY_EVENT.HOTKEY_SPEED_UP}`]() {
			const currentSpeedIndex = PLAYER_SPEED_OPTIONS.findIndex((v) => v === this.currentSpeed);
			if (currentSpeedIndex < PLAYER_SPEED_OPTIONS.length - 1) {
				this.player.setSpeed(PLAYER_SPEED_OPTIONS[currentSpeedIndex + 1]);
			}
		},
		[`BUS-${HOTKEY_EVENT.HOTKEY_SPEED_DOWN}`]() {
			const currentSpeedIndex = PLAYER_SPEED_OPTIONS.findIndex((v) => v === this.currentSpeed);
			if (currentSpeedIndex > 0) {
				this.player.setSpeed(PLAYER_SPEED_OPTIONS[currentSpeedIndex - 1]);
			}
		},
	},
};
</script>

<style lang="scss" scoped>
@import '@shared/sass/shared-variables';

.player-wrapper {
	position: relative;
	display: flex;
	flex: 1;
	max-width: 100%;
}
.player-wrapper-container {
	position: relative;
	display: flex;
	flex-direction: column;
	width: 100%;
	height: 100%;

	body.-t-light & {
		color: var(--color-white);
	}
}

.player-overlay {
	&.-is-preview .overlay-wrapper {
		pointer-events: none;
	}

	&.-is-preview .autoplay-muted-indicator {
		display: none;
	}

	&.-is-preview.-preview-started .autoplay-muted-indicator {
		display: flex;
	}
}

.player-wrapper__player-controls {
	@include z-index('player-controls', 'tagging-tool');
	position: relative;
}

</style>
